/**
 * Period-themed how-to-play modal. Long-form, scrollable, fits on a phone.
 * Opened from the lobby and from the at-sea screen.
 */

let modal: HTMLElement | null = null;
let keyHandler: ((e: KeyboardEvent) => void) | null = null;
let prevBodyOverflow = '';

export function openHowToPlay(): void {
  closeHowToPlay();

  modal = document.createElement('div');
  modal.id = 'howto-modal';
  modal.className = 'howto-modal';
  modal.innerHTML = `
    <div class="howto-modal-backdrop"></div>
    <div class="howto-modal-panel">
      <div class="howto-header">
        <h2>The Captain's Manual</h2>
        <button class="secondary" id="howto-close">Close</button>
      </div>
      <div class="howto-body">

        <section>
          <h3>The Voyage</h3>
          <p>You are a captain of a square-rigged merchantman in the year 1696. From a port
          in Europe you must cross the open Atlantic and make landfall at <strong>Tortuga</strong>,
          off the north coast of Hispaniola — historically a thirty- to fifty-day passage. The voyage
          here unfolds in <em>real wall-clock time</em>. Your ship sails on while you are logged
          out. Check in once a day, give the helm new orders, and come back when you must.</p>
        </section>

        <section>
          <h3>Embarkation</h3>
          <ul>
            <li>Choose your <strong>origin</strong> — Noble (5,000 reales), Veteran Sailor (1,500), or
              Penniless Buccaneer (500). The purse decides the ship.</li>
            <li>Choose your <strong>port</strong> — Cádiz and Lisbon are the easy crossings.
              Brest and Plymouth open into the Bay of Biscay. Genoa must thread the
              Strait of Gibraltar before the Atlantic begins.</li>
            <li>At the dock, lay in <strong>provisions</strong>: water, hardtack and salt pork,
              lemons against scurvy, rum to keep the men's spirits. Buy <em>spare</em>
              tools — saws snap and rods crack at sea.</li>
            <li>"<strong>Buy Recommended</strong>" lays in a known-good loadout for your purse.</li>
            <li>Once you cast off, you cannot return to your port of embarkation.</li>
          </ul>
        </section>

        <section>
          <h3>The Helm</h3>
          <ul>
            <li>Click the compass dial — or type into the heading box — to set a new course.</li>
            <li>Trim the sails: <strong>full</strong>, <strong>half</strong>, <strong>reefed</strong>,
              <strong>anchor</strong>, or <strong>row</strong>. Reefed sails ride a gale safer.
              Rowing crawls without wind but drains the men.</li>
            <li>The trade winds blow west-southwest. Hold a near-tailwind and you fly. Sail close-hauled and you crawl.</li>
          </ul>
        </section>

        <section>
          <h3>The Sea & the Weather</h3>
          <ul>
            <li><strong>Doldrums</strong> — the wind dies. The ship lies slack. Rowing or patience is your only choice.</li>
            <li><strong>Gales</strong> at 22 knots tear at the rigging.</li>
            <li><strong>Storms</strong> from 32 knots batter the hull — every storm shaves percentage points from your integrity.</li>
            <li><strong>Hurricanes</strong> haunt the western Atlantic basin from June through November.
              They are storms made worse — twice as deadly. Catch one with a half-broken hull and you sink.</li>
            <li><strong>Fog banks</strong> roll in occasionally. Visibility collapses to three kilometres until the fog lifts.</li>
            <li><strong>Currents</strong> — the Gulf Stream, the North Equatorial, the Canary,
              the North Atlantic Drift — push you whether you will or not. Check the chart for their effect on your wake.</li>
          </ul>
        </section>

        <section>
          <h3>The Crew</h3>
          <ul>
            <li>Every sailor is a man with a name. They die individually — to starvation, thirst, scurvy,
              cannon fire, mutineer's blade, or the sea.</li>
            <li><strong>Morale</strong> falls with hardship and rises with comfort. Rum, sunsets,
              the ship's cat, a glorious sunrise — all lift the men. Storms, deaths, and crime
              drag them down.</li>
            <li>If morale touches zero, the men may mutiny. They will not be merciful.</li>
            <li><strong>Scurvy</strong> begins after forty days without lemons or sauerkraut.
              A ship's surgeon and a medicine chest will hold it off twice as long.</li>
          </ul>
        </section>

        <section>
          <h3>Crime & the Brig</h3>
          <ul>
            <li>When morale slips, the brotherhood frays. Sailors steal from the rations.
              In the worst cases, men are found dead below decks.</li>
            <li>An accused sailor walks free unless you act. Loose offenders <em>strike again</em>
              — every twenty days, on average — and their crimes compound.</li>
            <li>Put them in <strong>irons</strong> below. The brig holds three men. While the brig
              is occupied, the rest of the crew grow restless and morale drains a little each day.</li>
            <li>Eight percent per day, a confined man may come round. A rehabilitated prisoner
              can be released without fear of repeat.</li>
          </ul>
        </section>

        <section>
          <h3>Tools Break</h3>
          <p>The carpenter's tools dull, the fishing rod cracks, the sextant's mirror chips. Each
          day a key tool may fail and be lost overboard. Without the carpenter's tools the hull
          stops mending; without the sextant your reckoning suffers in foul weather. Bring a spare
          set of the critical ones, or plan to land at an island shop and refit.</p>
        </section>

        <section>
          <h3>Combat</h3>
          <ul>
            <li>If you mount cannons, you may fire on another ship within range
              — three kilometres for six-pounders, five for twelves.</li>
            <li>Three shot types: <strong>round shot</strong> for hulls,
              <strong>grape</strong> for crew, <strong>chain</strong> for sails. Each volley
              consumes a measure of powder and your stock of that shot.</li>
            <li>Hoist the red flag to declare war; lower to the white for peace. Other captains see your colours.</li>
            <li>You can sink another captain. Their wreck will sit on the chart, with their name
              for as long as the world endures.</li>
          </ul>
        </section>

        <section>
          <h3>Trade & Islands</h3>
          <ul>
            <li>Within two kilometres of another captain you can <em>propose a trade</em> —
              goods for reales.</li>
            <li>Islands and coastal towns appear on your chart with a dotted ring. Drop the
              anchor inside the ring to land. Their shops will buy what they don't stock at half
              price — you can offload caught fish, whale meat, and cargo from any wreck you've salvaged.</li>
            <li>Tiny rocks have no shop. They may have salvage to find. They may also have
              hostile islanders.</li>
          </ul>
        </section>

        <section>
          <h3>The Chart</h3>
          <ul>
            <li>Open the <strong>Captain's Chart</strong> from the helm screen. The view is
              centered on your ship; click and drag to pan, scroll to zoom.</li>
            <li>Cells you have sailed within sight of stay lit forever. Land you have seen
              shows green. Sea you have crossed shows blue. The rest of the Atlantic is dark.</li>
            <li>Sightings (other ships, whales, islands) appear within your visual range.
              A telescope extends that range from twelve to thirty-five kilometres.</li>
            <li>Lost captains leave permanent grave-markers wherever they went down.</li>
          </ul>
        </section>

        <section>
          <h3>The Hazards</h3>
          <ul>
            <li><strong>Hurricanes</strong> — Caribbean basin, June through November. Ride them out at reefed sails.</li>
            <li><strong>Reefs</strong> — small islands are point hazards. Sail too close and the hull splits.</li>
            <li><strong>Coast warning</strong> — at twenty kilometres from any landmass (with sea charts) the lookout calls "land ho." Heed him.</li>
            <li><strong>Scurvy</strong> — forty days without antiscorbutics, then men begin to die.</li>
            <li><strong>Mutiny</strong> — at zero morale.</li>
            <li><strong>Pirates and privateers</strong> — most sails on the horizon are merchants. Some are not.</li>
          </ul>
        </section>

        <section>
          <h3>Arrival</h3>
          <p>When you sight your port and bring the ship within twenty-five kilometres of the
          anchorage, the crossing is done. The harbormaster asks the only question that matters:
          <em>staying, or shoving off?</em> Stay, and your ledger is sealed — days at sea, crew
          surviving, ships sunk, whales taken, reales in the chest — and the name goes back on the wall.
          Shove off, and you sell your cargo, refit at the full dockside, pick a port, and sail again:
          from Tortuga to any harbor in Europe, from Europe back to Tortuga, for as long as your
          stores and patience hold.</p>
          <p>Your chart remembers. Islands and wreck sites you have sighted stay marked for good.
          Ships, whales, and shoals of fish leave a “last seen” mark where they slipped out of
          sight — one per creature or hull — which is struck the moment you sight the same one again.</p>
        </section>

      </div>
    </div>
  `;
  document.body.appendChild(modal);

  // Lock body scroll
  prevBodyOverflow = document.body.style.overflow;
  document.body.style.overflow = 'hidden';

  // Close handlers
  modal.querySelector<HTMLButtonElement>('#howto-close')!.onclick = closeHowToPlay;
  modal.querySelector<HTMLElement>('.howto-modal-backdrop')!.onclick = closeHowToPlay;
  keyHandler = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') closeHowToPlay();
  };
  document.addEventListener('keydown', keyHandler);
}

export function closeHowToPlay(): void {
  if (!modal) return;
  if (keyHandler) {
    document.removeEventListener('keydown', keyHandler);
    keyHandler = null;
  }
  document.body.style.overflow = prevBodyOverflow;
  modal.remove();
  modal = null;
}
